import { GetProjectsFilter, ProjectSummary, ProjectType } from './../../../common/models/project';
import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { CurrentUser } from "../../../common/models/user";
import { ProjectService } from "../../../common/services/project.service";
import { StorageService } from "../../../common/services/storage.service";
import { SearchService } from "../../../common/services/search.service";
import { CustomizerSettingsService } from "../../../customizer-settings/customizer-settings.service";
import { Subject, takeUntil } from 'rxjs';
import { ProjectsFilterBuilder } from '../../../common/components/filter/builders/projects-filter-builder';
import { FilterService } from '../../../common/components/filter/services/filter.service';
import { ProjectStatus } from '../../../common/enums/project-status';

@Component({
    selector: 'app-projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.scss'],
    imports: [MatCardModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        MatExpansionModule,
        MatCheckboxModule,
        ReactiveFormsModule,
        CommonModule]
})
export class ProjectsComponent implements OnInit {
    private destroy$ = new Subject<void>();

    projects: ProjectSummary[] = [];
    filteredProjects: ProjectSummary[] = [];
    user: CurrentUser | null = null;

    public ProjectStatus = ProjectStatus;
    public ProjectType = ProjectType;
  
    isToggled = false;
    showLifecycleCtrl = new FormControl(false, { nonNullable: true });
  
    constructor(
      private router: Router,
      private filterBuilder: ProjectsFilterBuilder,
      private projectService: ProjectService,
      private storageService: StorageService,
      private searchService: SearchService,
      private filterService: FilterService,
      public themeService: CustomizerSettingsService
    ) {}
  
    ngOnInit() {
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });
      this.filterService.filterChanges$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
        this.filterService.setFiltersInScope(filters);
        this.fetchProjects();
      });
      this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
        this.user = user;
        this.initializeFilters()
      });
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

    initializeFilters(): void {
      const startYear = 2025;
      const currentYear = new Date().getFullYear();
    
      const config = this.filterBuilder
        .reset()
        .years(startYear)
        .build();

      this.filterService.setFilterConfig(config);
      this.filterService.restoreFiltersInScope({ year: currentYear });
    }
  
    fetchProjects(): void {
      const savedFilterValues = this.filterService.getFiltersInScope();

      const associationId = this.user?.associationId ?? '';
      const municipalityId = this.user?.municipalityId ?? '';

      var filter = new GetProjectsFilter(
        municipalityId, 
        associationId, 
        savedFilterValues?.year
      );
      
      this.projectService.getProjects(filter).pipe(takeUntil(this.destroy$)).subscribe({
        next: (data) => {
          this.projects = data.map(a => ProjectSummary.fromJson(a));
          this.filteredProjects = [...this.projects];
        },
        error: (error) => {
          console.error('Error fetching projects', error);
        }
      });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredProjects = this.projects.filter((project: ProjectSummary) => {
          return (
            project.organizationName.toLowerCase().includes(searchTerm) ||
            project.name.toLowerCase().includes(searchTerm) ||
            project.year.toString().includes(searchTerm)
          );
        });
      } else {
        this.filteredProjects = [...this.projects];
      }
    }

    goToProjectSaldo(project: ProjectSummary): void {
      this.router.navigate(['association', 'projects', project.id.toString(), 'saldo']);
    }

    goToProjectBudgets(project: ProjectSummary): void {
      this.router.navigate(['association', 'projects', project.id.toString(), 'budgets']);
    }

    goToProjectReports(project: ProjectSummary): void {
      this.router.navigate(['association', 'projects', project.id.toString(), 'reports']);
    }

    goToViewRefunds(project: ProjectSummary): void {
      this.router.navigate(['association', 'projects', project.id.toString(), 'refunds']);
    }
  }
